import { useState, useEffect } from "react";
import { supabase } from "../../../../../../integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../../ui/card";
import { Skeleton } from "../../../../../ui/skeleton";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";

interface SentimentChartProps {
  dateRange: string;
  platform: string;
  outletIds?: string[];
  supabaseoutletIDs: string[];
}

export const SentimentChart = ({ dateRange, platform, outletIds, supabaseoutletIDs }: SentimentChartProps) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any[]>([]);

  useEffect(() => {
    loadData();
  }, [dateRange, platform, outletIds]);

  const loadData = async () => {
    setLoading(true);
    try {
      const daysAgo = dateRange === "7days" ? 7 : dateRange === "30days" ? 30 : 90;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysAgo);

      let query = supabase
        .from("reviews")
        .select("sentiment")
        .gte("posted_at", startDate.toISOString());

      if (platform !== "all") {
        query = query.eq("platform", platform as any);
      }

      const isAllSelected = !outletIds || outletIds.length === 0 || outletIds.includes("all");
      if (!isAllSelected && outletIds.length > 0) {
        query = query.in("outlet_id", outletIds);
      }else {
        query = query.in("outlet_id", supabaseoutletIDs);
      }

      const { data: reviews, error } = await query;
      if (error) throw error;

      const sentimentCounts = reviews?.reduce((acc, r) => {
        const sentiment = r.sentiment || "neutral";
        acc[sentiment] = (acc[sentiment] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const chartData = [
        { name: "Positive", value: sentimentCounts?.positive || 0, color: "#10B981" },
        { name: "Neutral", value: sentimentCounts?.neutral || 0, color: "#F59E0B" },
        { name: "Negative", value: sentimentCounts?.negative || 0, color: "#EF4444" },
      ];

      setData(chartData);
    } catch (error) {
      console.error("Error loading sentiment data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sentiment Breakdown</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-[250px] w-full" />
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};
